import { useFormatters } from '@/hooks/useFormatters';
import { useGlobalModal } from '@/hooks/useGlobalModal';
import DeviceNameSpan from '@components/deviceNameSpan';
import GlobalModal from '@components/GlobalModal';
import ConfirmationModal from '@components/modal/ConfirmationModal';
import ModalActions from '@components/modal/ModalActions';
import ModalContent from '@components/modal/ModalContent';
import ModalTitle from '@components/modal/ModalTitle';
import { Access } from '@features/authorization';
import { formatPosition } from '@features/gateCommon/utils/formatPosition';
import { getMovementFragmentData } from '@features/gateCommon/utils/getMovementFragmentData';
import { msg, Trans } from '@lingui/macro';
import { useLingui } from '@lingui/react';
import { useCallback, useEffect, useState, type FC } from 'react';
import { AU_BACKDROP_SX, AU_DIALOG_PAPER_SX, AU_SUCCESS } from '@/theme/au-compat';
import { useGateLiveStatus } from '../hooks/useGateLiveStatus';
import { useMoveGate } from '../hooks/useMoveGate';
import { LiveBattery } from './LiveBattery';
import { LiveBlocks } from './LiveBlocks';
import { LiveNotice } from './LiveNotice';
import { LastPosition } from './LastPosition';
import { MoveGateForm } from './MoveGateForm';
import { OperatingState } from './OperatingState';
import { Scenario } from './Scenario';

/**
 * Los dos diálogos del paso «Definir operación» llevan el mismo chrome de la capa AU. Se declara
 * una vez: la confirmación es el segundo paso del mismo flujo y en el especimen es el mismo
 * `Dialog`, sólo con `role="alertdialog"`.
 *
 * `size` es el eje de ancho de `.au-dialog`: `md` son 460px y `lg` 680. «Definir operación» va en
 * `lg` —lleva el escenario— y la confirmación en `md`, que es el default de la capa.
 */
const auDialogProps = (maxWidth: 460 | 680) => ({
  maxWidth: false as const,
  PaperProps: { sx: { ...AU_DIALOG_PAPER_SX, width: '100%', maxWidth } },
  slotProps: { backdrop: { sx: AU_BACKDROP_SX } },
});

export const MoveGateModal: FC = () => {
  const { _ } = useLingui();
  const { formatMetricValue } = useFormatters();
  const { globalModalProps, state, onClose } = useGlobalModal('moveGateModal');
  const { setModalState: setOperationModal } = useGlobalModal('gateOperationModal');
  const [isValid, setIsValid] = useState(false);
  const [openConfirmation, setOpenConfirmation] = useState(false);
  const [initialPosition, setInitialPosition] = useState(0);
  const [position, setPosition] = useState('');

  const { moveGate, loading } = useMoveGate({
    // El hook ya notificó al operario el desenlace del comando (AMARU-862/US-004). Acá solo se
    // decide la navegación: una aceptación con `movement` abre el modal de operación, y también
    // una solicitud que quedó en cola (`QUEUED`, AMARU-897) —el panel la muestra «En cola» y deja
    // cancelarla—. Un no-op (GO03), un rechazo, `ALREADY_QUEUED` o un error de transporte llegan
    // sin `movement` y dejan este modal abierto con los datos puestos, para reintentar sin volver
    // a armar la operación.
    onResult({ outcome, movement }) {
      setOpenConfirmation(false);
      if ((outcome !== 'accepted' && outcome !== 'queued') || !movement) return;
      const movementData = getMovementFragmentData(movement);
      onClose();
      setOperationModal({
        open: true,
        movementId: movementData.id,
      });
    },
  });

  const gateId = state.open ? state.gateId : null;
  // Fuente de verdad del modal: una consulta por apertura, compartida por todas las filas.
  const liveStatus = useGateLiveStatus(gateId);
  // El equipo reporta una operación que la base todavía no ve: no se pide otra encima. Ni un
  // refresco en curso ni uno fallido lo liberan (`lastKnownMoving`).
  const deviceMoving = liveStatus.lastKnownMoving;
  const deviceSilent = liveStatus.lastDeviceSilent;

  useEffect(() => {
    setIsValid(false);
    setPosition('');
  }, [state]);

  const onSubmit = useCallback(
    (needConfirmation?: boolean) => {
      if (!isValid || !gateId || deviceMoving) return;
      const positionNumber = parseFloat(position);
      if (isNaN(positionNumber)) return;
      if (needConfirmation) {
        setOpenConfirmation(true);
      } else {
        moveGate(gateId, positionNumber);
      }
    },
    [isValid, position, gateId, moveGate, deviceMoving],
  );

  return (
    <GlobalModal
      {...globalModalProps}
      dialogProps={auDialogProps(680)}
      authorization={
        state.open ? { access: Access.OperateGate, deviceId: state.gateId } : undefined
      }
    >
      {gateId && (
        <ConfirmationModal
          open={openConfirmation}
          onClose={() => setOpenConfirmation(false)}
          title={<DeviceNameSpan deviceId={gateId}></DeviceNameSpan>}
          dialogProps={auDialogProps(460)}
          content={
            <>
              <Trans>
                ¿Esta seguro que desea mover la compuerta desde{' '}
                <b>{formatPosition(initialPosition, formatMetricValue)}</b> a{' '}
                <b style={{ color: AU_SUCCESS }}>
                  {formatPosition(Number(position), formatMetricValue)}
                </b>
                ?
              </Trans>
              {deviceSilent && (
                <>
                  {' '}
                  <Trans>
                    El equipo está desconectado: la solicitud quedará en cola hasta que se conecte.
                  </Trans>
                </>
              )}
            </>
          }
          submitButton={{
            onClick: () => onSubmit(),
            loading,
            label: deviceSilent ? _(msg`Encolar`) : undefined,
          }}
        />
      )}
      <ModalTitle
        onClose={onClose}
        title={<Trans>Definir operación</Trans>}
        subtitle={
          gateId ? (
            <>
              {/* `.oc-dialog-title small`: `{gate.id} — {gate.name}`, con raya y no guion corto. */}
              {gateId} — <DeviceNameSpan deviceId={gateId} />
            </>
          ) : (
            <Trans>Sin compuerta seleccionada</Trans>
          )
        }
      >
        <LiveBattery gateId={gateId} live={liveStatus} />
      </ModalTitle>
      {/* `.oc-define`: la grilla y el gap los pone `AU_DIALOG_PAPER_SX` para los dos diálogos. */}
      <ModalContent>
        <Scenario />
        <OperatingState gateId={gateId} live={liveStatus} />
        <LastPosition gateId={gateId} live={liveStatus} />
        <LiveBlocks live={liveStatus} />
        <LiveNotice gateId={gateId} live={liveStatus} />
        <MoveGateForm
          gateId={gateId}
          firmwareLimits={liveStatus.firmwareLimits}
          livePosition={liveStatus.formReading?.position ?? null}
          awaitingDevice={liveStatus.initialLoading}
          refreshing={liveStatus.refreshing}
          setIsValid={setIsValid}
          position={position}
          setPosition={setPosition}
          setInitialPosition={setInitialPosition}
        />
      </ModalContent>
      <ModalActions
        onClose={onClose}
        onSubmit={() => onSubmit(true)}
        submitDisabled={!isValid || isNaN(parseFloat(position)) || deviceMoving}
        // Sin respuesta del equipo el comando no se ejecuta ya: `operate-gate` lo encola.
        submitLabel={deviceSilent ? _(msg`Encolar operación`) : _(msg`Operar compuerta`)}
        closeButton={{ label: _(msg`Cancelar`) }}
      />
    </GlobalModal>
  );
};
